import getWindow from 'Utils/get-window';

/**
 * Render-from-cache for flint web header bidding — the browser half of the
 * standard universal-creative pattern (same shape as Prebid Universal
 * Creative).
 *
 * flint's FLCPM price-priority line items can win the GAM auction, but the
 * mobile FLCPM creatives call into the app SDK and cannot render in a
 * browser. Instead, flint.ts caches the winning bid's HTML markup here at
 * query time (the /query response already contains it at ad.item.script for
 * rich_media_third_party ads), and a tiny universal HTML creative trafficked
 * on the FLCPM line items runs inside the ad slot's iframe when flint wins
 * and calls back into this page to render the cached markup for its
 * %%PATTERN:flint_ad_id%% (see flint-web-universal-creative HTML, kept with
 * the GAM trafficking docs — it is not part of this build).
 *
 * Two callback paths, mirroring Prebid:
 *
 * 1. Friendly iframe (GAM default for custom HTML creatives): the creative
 *    calls window.parent.__flipboardRenderFlintAd(flintAdId, window) and we
 *    document.write the cached markup into the creative's own document,
 *    resizing its iframe to the bid size.
 * 2. SafeFrame (cross-origin — `parent` is unreachable): the creative posts
 *    {type: 'flint-render', flintAdId} and the page REPLIES to the SafeFrame
 *    with {type: 'flint-render-response', markup, ...}; the creative then
 *    document.writes the markup into its own SafeFrame document. The bid
 *    JS stays inside SafeFrame's cross-origin sandbox — the page never
 *    executes creative markup on the flipboard.com origin. (The page also
 *    best-effort resizes the SafeFrame's host iframe to the bid size, which
 *    is layout-only and safe.)
 *
 * Everything fails soft: a cache miss (expired SPA page, macro not
 * populated) is a no-op and the slot just shows GAM's blank render.
 */

export type FlintCachedAd = {
  // The winning bid's HTML markup (OpenRTB bid.adm), ready to document.write.
  markup: string;
  width: number;
  height: number;
};

export const FLINT_RENDER_MESSAGE_TYPE = 'flint-render';
export const FLINT_RENDER_RESPONSE_MESSAGE_TYPE = 'flint-render-response';
export const FLINT_RENDER_HOOK_NAME = '__flipboardRenderFlintAd';

// Structural (not lib.dom) types so SSR-safe getWindow() and unit-test fakes
// both typecheck; every property is optional and runtime-checked.
type CreativeDocumentLike = {
  open?: () => void;
  write?: (markup: string) => void;
  close?: () => void;
};

type FrameElementLike = {
  setAttribute?: (name: string, value: string) => void;
  style?: Record<string, string>;
  parentElement?: {
    ownerDocument?: {
      createElement?: (tag: string) => FrameElementLike;
    } | null;
    appendChild?: (node: unknown) => void;
  } | null;
  contentWindow?: { document?: CreativeDocumentLike | null } | null;
};

type CreativeWindowLike = {
  document?: CreativeDocumentLike | null;
  frameElement?: FrameElementLike | null;
};

type MessageSourceLike = {
  postMessage?: (message: unknown, targetOrigin: string) => void;
};

type RenderMessageEventLike = {
  data?: unknown;
  source?: unknown;
};

type RendererWindow = {
  [FLINT_RENDER_HOOK_NAME]?: (
    flintAdId: string,
    creativeWindow: unknown,
  ) => boolean;
  addEventListener?: (
    type: string,
    listener: (event: RenderMessageEventLike) => void,
  ) => void;
  document?: {
    querySelectorAll?: (selector: string) => ArrayLike<FrameElementLike>;
  } | null;
};

const getRendererWindow = (): RendererWindow =>
  getWindow() as unknown as RendererWindow;

// Cached winning-bid creatives keyed by flint_ad_id. Small FIFO cap: only
// bids fetched on the current page can win the concurrently-running GAM
// auction, so anything older is dead weight.
const MAX_CACHED_ADS = 20;
const cachedAds = new Map<string, FlintCachedAd>();

export const getCachedFlintAd = (flintAdId: string): FlintCachedAd | null =>
  cachedAds.get(flintAdId) || null;

/** Test-only reset (module-level cache would otherwise leak across tests). */
export const clearCachedFlintAds = (): void => {
  cachedAds.clear();
};

/**
 * Cache a winning bid's creative so the universal creative can render it if
 * flint wins the GAM auction. Also (idempotently) installs the render hooks
 * — a creative can only ever fire after a bid was cached, so installing here
 * is always early enough.
 */
export const cacheFlintAd = (flintAdId: string, ad: FlintCachedAd): void => {
  if (!flintAdId || !ad || !ad.markup) {
    return;
  }
  // Re-insert so eviction order stays "least recently cached first".
  cachedAds.delete(flintAdId);
  cachedAds.set(flintAdId, ad);
  while (cachedAds.size > MAX_CACHED_ADS) {
    const oldest = cachedAds.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    cachedAds.delete(oldest);
  }
  installFlintRenderer();
};

// Size an iframe (host GAM iframe or our fallback iframe) to the bid.
const sizeFrame = (frame: FrameElementLike, ad: FlintCachedAd): void => {
  frame.setAttribute?.('width', String(ad.width));
  frame.setAttribute?.('height', String(ad.height));
  if (frame.style) {
    frame.style.width = `${ad.width}px`;
    frame.style.height = `${ad.height}px`;
    frame.style.border = '0';
  }
};

const writeMarkup = (doc: CreativeDocumentLike, ad: FlintCachedAd): boolean => {
  if (typeof doc.write !== 'function') {
    return false;
  }
  // document.open/write/close into the (friendly) iframe document is the
  // standard header-bidding render path — it replaces the creative stub with
  // the bid markup AND executes its scripts (innerHTML would not).
  doc.open?.();
  doc.write(ad.markup);
  doc.close?.();
  return true;
};

/**
 * Friendly-iframe render path, exposed on the page as
 * window.__flipboardRenderFlintAd(flintAdId, creativeWindow). Called by the
 * universal creative with its own window; writes the cached markup into the
 * creative's document and resizes its iframe to the bid size. Returns false
 * (leaving GAM's blank render) on a cache miss.
 */
export const renderFlintAd = (
  flintAdId: string,
  creativeWindow: unknown,
): boolean => {
  const ad = getCachedFlintAd(String(flintAdId));
  const win = creativeWindow as CreativeWindowLike | null;
  const doc = win?.document;
  if (!ad || !doc) {
    // Cache miss (expired SPA page, unexpanded macro): silent no-op — the
    // slot keeps GAM's blank render.
    return false;
  }
  try {
    // GAM sizes the iframe to the creative's declared size (1x1 for the
    // size-override universal creative), so resize it to the bid.
    const frame = win?.frameElement;
    if (frame) {
      sizeFrame(frame, ad);
    }
  } catch (_) {
    // frameElement access can throw in exotic frame setups; markup still
    // renders at whatever size GAM gave the iframe.
  }
  return writeMarkup(doc, ad);
};

/**
 * SafeFrame fallback: the creative cannot reach `parent`, so it posts
 * {type: 'flint-render', flintAdId} instead. Reply to the SafeFrame with the
 * cached markup ({type: 'flint-render-response'}) and let the creative
 * document.write it into its OWN sandboxed document — the bid's JavaScript
 * never executes on the flipboard.com origin (SafeFrame isolation is
 * preserved; do NOT render creative markup into a same-origin frame here).
 * The page's only DOM involvement is a best-effort, layout-only resize of
 * the SafeFrame's host iframe to the bid size.
 *
 * The reply is gated on the message source actually being an iframe in this
 * document (window-reference comparison works cross-origin), so arbitrary
 * cross-origin windows can't probe the markup cache.
 */
export const handleFlintRenderMessage = (
  event: RenderMessageEventLike,
): void => {
  const data = event?.data as {
    type?: unknown;
    flintAdId?: unknown;
  } | null;
  if (
    !data ||
    data.type !== FLINT_RENDER_MESSAGE_TYPE ||
    (typeof data.flintAdId !== 'string' && typeof data.flintAdId !== 'number')
  ) {
    return;
  }
  const flintAdId = String(data.flintAdId);
  const ad = getCachedFlintAd(flintAdId);
  if (!ad) {
    // Cache miss: silent no-op — the slot keeps GAM's blank render.
    return;
  }
  const source = event.source as MessageSourceLike | null;
  if (!source || typeof source.postMessage !== 'function') {
    return;
  }
  const doc = getRendererWindow().document;
  if (!doc || typeof doc.querySelectorAll !== 'function') {
    return;
  }
  const iframes = doc.querySelectorAll('iframe');
  let hostIframe: FrameElementLike | null = null;
  for (let i = 0; i < iframes.length; i++) {
    if (iframes[i].contentWindow === event.source) {
      hostIframe = iframes[i];
      break;
    }
  }
  if (!hostIframe) {
    // Not a frame of ours — don't hand markup to unknown windows.
    return;
  }
  sizeFrame(hostIframe, ad);
  // targetOrigin '*': the SafeFrame origin is Google's and not statically
  // knowable; the payload is the bidder's own public creative markup (no
  // user or page data), already validated to go to one of our own iframes.
  source.postMessage(
    {
      type: FLINT_RENDER_RESPONSE_MESSAGE_TYPE,
      flintAdId,
      markup: ad.markup,
      width: ad.width,
      height: ad.height,
    },
    '*',
  );
};

/**
 * Install the page-side render hooks (idempotent — keyed off the window so
 * it also re-installs on a fresh window in tests). Only ever called after a
 * bid has been cached, i.e. client-side only.
 */
export const installFlintRenderer = (): void => {
  const win = getRendererWindow();
  if (!win || win[FLINT_RENDER_HOOK_NAME]) {
    return;
  }
  win[FLINT_RENDER_HOOK_NAME] = renderFlintAd;
  if (typeof win.addEventListener === 'function') {
    win.addEventListener('message', handleFlintRenderMessage);
  }
};
